import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area,
} from 'recharts';
import {
  TrendingUp, TrendingDown, MapPin, Package, Truck, Users, Activity,
  Clock, XCircle, Download, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#1D5CA5', '#2F9E8F', '#F59E0B', '#64748B', '#3B82F6', '#EC4899', '#8B5CF6'];
const STATUS_COLORS = {
  pending: '#F59E0B', paid: '#3B82F6', confirmed: '#22C55E',
  rejected: '#EF4444', cancelled: '#64748B', expired: '#9CA3AF',
};
const STATUS_LABELS = {
  pending: 'Ausstehend', paid: 'Bezahlt', confirmed: 'Bestätigt',
  rejected: 'Abgelehnt', cancelled: 'Storniert', expired: 'Abgelaufen',
};

const AdminAnalytics = () => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(await res.json());
    } catch (e) { console.error('Analytics error:', e); }
    finally { setLoading(false); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `buchungen-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) { console.error('Export error:', e); }
    finally { setExporting(false); }
  };

  const fmt = (price) => new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-[hsl(var(--border))]">
        <p className="font-medium text-sm">{label}</p>
        {payload.map((e, i) => (
          <p key={i} className="text-sm" style={{ color: e.color }}>
            {e.name}: {e.name.includes('msatz') || e.name.includes('evenue') || e.name.includes('Umsatz') ? `CHF ${fmt(e.value)}` : e.value}{e.name.includes('%') || e.name.includes('rate') || e.name.includes('Rate') ? '%' : ''}
          </p>
        ))}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const totalRevenue = analytics?.monthly_revenue?.reduce((s, m) => s + m.revenue, 0) || 0;
  const totalBookings = analytics?.monthly_revenue?.reduce((s, m) => s + m.bookings, 0) || 0;
  const avgDuration = analytics?.avg_booking_duration || 0;
  const statusData = (analytics?.status_distribution || []).map(item => ({
    name: STATUS_LABELS[item.status] || item.status, value: item.count, fill: STATUS_COLORS[item.status] || '#64748B',
  }));

  // Latest growth rate
  const growthRates = analytics?.growth_rates || [];
  const latestGrowth = growthRates.length > 0 ? growthRates[growthRates.length - 1].growth_percent : 0;

  return (
    <div data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Analytics & Berichte</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Detaillierte Einblicke in Ihr Geschäft</p>
        </div>
        <Button variant="outline" onClick={exportCSV} disabled={exporting} data-testid="analytics-export-csv">
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exportieren...' : 'CSV Export'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          {
            label: 'Gesamtumsatz (12 Mo)', value: `CHF ${fmt(totalRevenue)}`,
            icon: TrendingUp, color: 'chart-2',
            sub: latestGrowth !== 0 ? `${latestGrowth > 0 ? '+' : ''}${latestGrowth}% ggü. Vormonat` : null,
            subPositive: latestGrowth >= 0,
          },
          { label: 'Buchungen (12 Mo)', value: totalBookings, icon: MapPin, color: 'primary' },
          { label: 'Durchschnitt / Buchung', value: `CHF ${totalBookings > 0 ? fmt(totalRevenue / totalBookings) : 0}`, icon: Package, color: 'chart-3' },
          { label: 'Durchschn. Dauer', value: `${avgDuration} Tage`, icon: Clock, color: 'chart-5', sub: `${Math.round(avgDuration / 7 * 10) / 10} Wochen` },
        ].map((card, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card className="card-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{card.label}</p>
                    <p className="text-2xl font-semibold mt-1">{card.value}</p>
                    {card.sub && (
                      <p className={`text-xs mt-1 flex items-center gap-0.5 ${card.subPositive === false ? 'text-red-500' : card.subPositive === true ? 'text-emerald-600' : 'text-[hsl(var(--muted-foreground))]'}`}>
                        {card.subPositive === true && <ArrowUpRight className="w-3 h-3" />}
                        {card.subPositive === false && <ArrowDownRight className="w-3 h-3" />}
                        {card.sub}
                      </p>
                    )}
                  </div>
                  <div className={`w-11 h-11 bg-[hsl(var(--${card.color})/0.1)] rounded-xl flex items-center justify-center`}>
                    <card.icon className={`w-5 h-5 text-[hsl(var(--${card.color}))]`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Revenue + Bookings Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Monatlicher Umsatz</CardTitle>
            <CardDescription>Umsatzentwicklung der letzten 12 Monate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.monthly_revenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Umsatz" fill="#1D5CA5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Buchungstrend</CardTitle>
            <CardDescription>Anzahl Buchungen pro Monat</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics?.monthly_revenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="bookings" name="Buchungen" stroke="#2F9E8F" strokeWidth={2} dot={{ fill: '#2F9E8F' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NEW: Cancellation Rate + Growth */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="card-shadow" data-testid="cancellation-rate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-[hsl(var(--destructive))]" />
              Stornierungsquote
            </CardTitle>
            <CardDescription>Stornierungen pro Monat (letzte 6 Monate)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.cancellation_rate || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="rate" name="Stornierungsrate %" stroke="#EF4444" fill="#EF4444" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {analytics?.cancellation_rate?.length > 0 && (
              <div className="flex gap-4 mt-3 text-xs text-[hsl(var(--muted-foreground))]">
                {analytics.cancellation_rate.slice(-3).map((m, i) => (
                  <span key={i}>{m.month}: <strong>{m.cancelled}/{m.total}</strong> ({m.rate}%)</span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-shadow" data-testid="growth-rate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[hsl(var(--chart-2))]" />
              Umsatzwachstum (MoM)
            </CardTitle>
            <CardDescription>Monat-zu-Monat Veränderung</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growthRates}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="growth_percent" name="Wachstum %" radius={[4, 4, 0, 0]}>
                    {growthRates.map((entry, idx) => (
                      <Cell key={idx} fill={entry.growth_percent >= 0 ? '#22C55E' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Utilization + Revenue by Trailer */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="card-shadow" data-testid="utilization-by-location-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[hsl(var(--primary))]" />
              Auslastung pro Standort
            </CardTitle>
            <CardDescription>Nächste 30 Tage</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.utilization_by_location?.length > 0 ? (
              <div className="space-y-4">
                {analytics.utilization_by_location.map((item) => (
                  <div key={item.location_id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{item.location_name}</span>
                      <span className="text-[hsl(var(--muted-foreground))]">{item.utilization_percent}% ({item.booked_days}d)</span>
                    </div>
                    <div className="w-full h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{
                        width: `${Math.min(100, item.utilization_percent)}%`,
                        backgroundColor: item.utilization_percent > 70 ? '#22C55E' : item.utilization_percent > 30 ? '#F59E0B' : '#EF4444',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Keine Standorte vorhanden</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-shadow" data-testid="revenue-by-trailer-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[hsl(var(--chart-2))]" />
              Umsatz pro Anhänger
            </CardTitle>
            <CardDescription>Top-Anhänger nach Umsatz</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.revenue_by_trailer?.length > 0 ? (
              <div className="space-y-4">
                {analytics.revenue_by_trailer.map((item, idx) => (
                  <div key={item.trailer_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <div>
                        <p className="font-medium">{item.trailer_name}</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.bookings} Buchungen</p>
                      </div>
                    </div>
                    <p className="font-semibold">CHF {fmt(item.revenue)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Noch keine Daten</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cohort + Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="card-shadow" data-testid="cohort-analysis-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[hsl(var(--chart-5))]" />
              Kundenkohorte
            </CardTitle>
            <CardDescription>Neue vs. wiederkehrende Kunden (6 Monate)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.cohort_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="new_customers" name="Neue Kunden" fill="#3B82F6" radius={[4, 4, 0, 0]} stackId="a" />
                  <Bar dataKey="returning_customers" name="Wiederkehrend" fill="#22C55E" radius={[4, 4, 0, 0]} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow" data-testid="status-distribution-card">
          <CardHeader>
            <CardTitle>Buchungsstatus-Verteilung</CardTitle>
            <CardDescription>Alle Buchungen nach Status</CardDescription>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                      {statusData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]"><p>Noch keine Daten</p></div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* NEW: Top Customers + Revenue by Location + Services */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="card-shadow" data-testid="top-customers-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[hsl(var(--primary))]" />
              Top-5 Kunden
            </CardTitle>
            <CardDescription>Nach Umsatz</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.top_customers?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_customers.map((c, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-6 h-6 rounded-full bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{c.name}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{c.bookings} Buchungen</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="ml-2 flex-shrink-0">CHF {fmt(c.total_spent)}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]"><p>Noch keine Daten</p></div>
            )}
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Umsatz nach Standort</CardTitle>
            <CardDescription>Top-Standorte</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.revenue_by_location?.length > 0 ? (
              <div className="space-y-3">
                {analytics.revenue_by_location.map((item, idx) => (
                  <div key={item.location_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <div>
                        <p className="font-medium text-sm">{item.location_name}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{item.bookings} Buchungen</p>
                      </div>
                    </div>
                    <p className="font-semibold text-sm">CHF {fmt(item.revenue)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]"><p>Noch keine Daten</p></div>
            )}
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Zusatzservices</CardTitle>
            <CardDescription>Beliebtheit</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.services_breakdown?.length > 0 ? (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={analytics.services_breakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="count" nameKey="service" label={({ service, count }) => `${service}: ${count}`}>
                      {analytics.services_breakdown.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Noch keine Services</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <Card className="card-shadow" data-testid="location-performance-card">
          <CardHeader>
            <CardTitle>Standort-Performance im Detail</CardTitle>
            <CardDescription>Umsatz und Auslastung in den nächsten 30 / 90 Tagen</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(analytics?.location_performance || []).slice(0, 8).map((item) => (
                <div key={item.location_id} className="rounded-lg border border-[hsl(var(--border))] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{item.location_name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{item.paid_bookings} Buchungen · CHF {fmt(item.paid_revenue)}</p>
                    </div>
                    <div className="text-right text-xs text-[hsl(var(--muted-foreground))]">
                      <p>30 Tage: <strong>{item.utilization_30}%</strong></p>
                      <p>90 Tage: <strong>{item.utilization_90}%</strong></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow" data-testid="funnel-analytics-card">
          <CardHeader>
            <CardTitle>Conversion-Funnel</CardTitle>
            <CardDescription>Anfrage → Offerte → Buchung → bezahlt / bestätigt</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Anfragen', analytics?.funnel?.inquiries_total || 0],
                ['Offerten', analytics?.funnel?.offers_created || 0],
                ['Versendet', analytics?.funnel?.offers_sent || 0],
                ['Akzeptiert', analytics?.funnel?.offers_accepted || 0],
                ['Buchungen', analytics?.funnel?.bookings_created || 0],
                ['Bezahlt', analytics?.funnel?.paid_bookings || 0],
                ['Bestätigt', analytics?.funnel?.confirmed_bookings || 0],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-[hsl(var(--border))] p-4">
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{label}</p>
                  <p className="text-2xl font-semibold mt-1">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-shadow mt-8" data-testid="campaign-performance-card">
        <CardHeader>
          <CardTitle>Kampagnenvergleich</CardTitle>
          <CardDescription>Geplante, veröffentlichte und fehlgeschlagene Social-Posts je Kampagne</CardDescription>
        </CardHeader>
        <CardContent>
          {(analytics?.campaign_performance || []).length > 0 ? (
            <div className="space-y-3">
              {analytics.campaign_performance.map((campaign) => (
                <div key={campaign.campaign_name} className="rounded-xl border border-[hsl(var(--border))] p-4 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-medium">{campaign.campaign_name}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Facebook: {campaign.facebook} · Instagram: {campaign.instagram} · Retries: {campaign.retries}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline">Queue: {campaign.queued}</Badge>
                    <Badge variant="outline">Geplant: {campaign.scheduled}</Badge>
                    <Badge>Veröffentlicht: {campaign.published}</Badge>
                    <Badge variant="secondary">Fehler: {campaign.failed}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">Noch keine Kampagnendaten vorhanden.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
